import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  TextField,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  MusicNote as MusicNoteIcon, 
  Save as SaveIcon, 
  Cancel as CancelIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  SmartToy as SmartToyIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  NavigateBefore,
  NavigateNext,
  Star as StarIcon,
  StarBorder as StarBorderIcon
} from '@mui/icons-material';
import ImageGrid from '../shared/ImageGrid';
import AIChat, { DRAWER_WIDTH } from '../../chat/AIChat';
import api from '../../../services/api';

export default function BlocMusic({ block, sessionId, sequenceIndex, sequence, onRefresh, onSave, openRegenerateDialogForBlock = null, onRegenerateDialogOpened = null }) {
  
  // Get music data from bloc_data
  const musicData = block.bloc_data || {};
  const musicUrl = musicData.url || '';
  const musicName = musicData.name || 'Sans titre';
  
  // Editing state
  const [editingName, setEditingName] = useState(false);
  const [editedName, setEditedName] = useState(musicName);
  const [isSaving, setIsSaving] = useState(false);
  
  // Image regeneration modal state
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [regeneratePrompt, setRegeneratePrompt] = useState('');
  const [regenerateModel, setRegenerateModel] = useState('google/gemini-2.5-flash-image');
  const [regenerateStyle, setRegenerateStyle] = useState('cartoon');
  const [regenerateAspectRatio, setRegenerateAspectRatio] = useState('16:9');
  const [regenerateNbVariations, setRegenerateNbVariations] = useState(2);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [imageEditingModels, setImageEditingModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  
  // Image titles state - map of image index to title
  const [imageTitles, setImageTitles] = useState(new Map());
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [editingTitleIndex, setEditingTitleIndex] = useState(null);
  
  // AI Chat state
  const [aiChatOpen, setAiChatOpen] = useState(true);
  
  // Advanced parameters state
  const [showAdvancedParams, setShowAdvancedParams] = useState(false);
  
  // Image carousel state
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  
  // Menu state
  const [imageMenuAnchor, setImageMenuAnchor] = useState(null);
  
  // Image filter state (for ImageGrid)
  const [imageFilter, setImageFilter] = useState('validated');
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [selectedImageIndices, setSelectedImageIndices] = useState(new Set());
  const [pendingImageValidations, setPendingImageValidations] = useState(new Map());
  const [isSavingValidation, setIsSavingValidation] = useState(false);

  // Image deletion state
  const [deleteImagesDialogOpen, setDeleteImagesDialogOpen] = useState(false);
  const [isDeletingImages, setIsDeletingImages] = useState(false);

  // Image reordering state (drag and drop)
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [reorderedImages, setReorderedImages] = useState(null); // null means no reordering, array means pending reorder
  
  // Get images from bloc_data (after state declarations)
  const originalImages = block.bloc_data?.images || block.content?.images || [];
  // Use reordered images if available, otherwise use original
  const images = reorderedImages || originalImages;
  
  // Get bloc_index from block
  const blocIndex = block.bloc_index;
  
  // Get effective validation status (considering pending changes)
  const getEffectiveValidationStatus = (index) => {
    const pendingStatus = pendingImageValidations.get(index);
    if (pendingStatus !== undefined) {
      return pendingStatus === 'validated';
    }
    return images[index]?.status_seance === 'validated';
  };
  
  // Get original indices of filtered images
  const getOriginalIndex = (filteredIndex) => {
    // If showing all images, return the index directly
    if (imageFilter === 'all') {
      return filteredIndex;
    }
    
    let count = 0;
    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      const isValidated = image.status_seance === 'validated';
      let shouldInclude = true;
      if (imageFilter === 'validated') shouldInclude = isValidated;
      if (imageFilter === 'not-validated') shouldInclude = !isValidated;
      
      if (shouldInclude) {
        if (count === filteredIndex) return i;
        count++;
      }
    }
    return filteredIndex;
  };
  
  // ImageGrid handlers
  const handleDragStart = (e, index) => {
    setDraggedImageIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };
  
  const handleDragLeave = () => {
    setDragOverIndex(null);
  };
  
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedImageIndex === null || draggedImageIndex === dropIndex) {
      setDraggedImageIndex(null);
      setDragOverIndex(null);
      return;
    }
    
    // Reorder images locally (don't save immediately)
    const newReorderedImages = [...images];
    const [movedImage] = newReorderedImages.splice(draggedImageIndex, 1);
    newReorderedImages.splice(dropIndex, 0, movedImage);
    
    // Update reordered images state (will be saved when user clicks save button)
    setReorderedImages(newReorderedImages);
    
    setDraggedImageIndex(null);
    setDragOverIndex(null);
  };
  
  const handleDragEnd = () => {
    setDraggedImageIndex(null);
    setDragOverIndex(null);
  };
  
  const handleToggleImageSelection = (index) => {
    const newSelected = new Set(selectedImageIndices);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedImageIndices(newSelected);
  };
  
  const handleToggleImageValidation = (index) => {
    setPendingImageValidations(prev => {
      const newMap = new Map(prev);
      const currentImage = images[index];
      const currentStatus = currentImage.status_seance;
      const pendingStatus = newMap.get(index);
      
      // If there's a pending change, remove it (revert)
      if (pendingStatus !== undefined) {
        newMap.delete(index);
      } else {
        // Toggle validation status
        const newStatus = currentStatus === 'validated' ? null : 'validated';
        newMap.set(index, newStatus);
      }
      return newMap;
    });
  };

  // Save all pending validations and reordering to backend
  const handleSaveValidations = async () => {
    if ((pendingImageValidations.size === 0 && !reorderedImages) || !onSave) return;
    
    setIsSavingValidation(true);
    try {
      let updatedImages = images.map((image, index) => {
        const pendingStatus = pendingImageValidations.get(index);
        if (pendingStatus !== undefined) {
          return { ...image, status_seance: pendingStatus };
        }
        return image;
      });
      
      // Update image_index for reordered images
      if (reorderedImages) {
        updatedImages = updatedImages.map((image, index) => ({
          ...image,
          image_index: index
        }));
      }
      
      const updatedBlock = {
        ...block,
        bloc_data: {
          ...block.bloc_data,
          images: updatedImages
        }
      };
      
      await onSave(updatedBlock);
      setPendingImageValidations(new Map());
      setReorderedImages(null); // Clear reordering after save
      
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error saving validations:', error);
      alert('Erreur lors de la sauvegarde des validations. Veuillez réessayer.');
    } finally {
      setIsSavingValidation(false);
    }
  };
  
  // Handle image regeneration modal
  const handleOpenRegenerateDialog = (imageIndex) => {
    setSelectedImageIndex(imageIndex);
    const images = block.bloc_data?.images || block.content?.images || [];
    const selectedImage = images[imageIndex];
    // Try to get the original prompt from the image metadata or block data
    setRegeneratePrompt(block.bloc_data?.prompt || block.content?.prompt || '');
    
    // Initialize image titles from existing images
    const titlesMap = new Map();
    images.forEach((image, idx) => {
      titlesMap.set(idx, image.title || '');
    });
    setImageTitles(titlesMap);
    
    setRegenerateDialogOpen(true);
  };

  // Effect to open regenerate dialog when triggered from parent
  useEffect(() => {
    if (openRegenerateDialogForBlock && openRegenerateDialogForBlock.blockId === block.id) {
      const images = block.bloc_data?.images || block.content?.images || [];
      // Wait for images to be available (they might be loading after generation)
      if (images.length > 0) {
        // Use the specified image index, or default to the last image
        const imageIndex = openRegenerateDialogForBlock.imageIndex !== undefined 
          ? openRegenerateDialogForBlock.imageIndex 
          : images.length - 1;
        // Ensure index is within bounds
        const safeIndex = Math.min(Math.max(0, imageIndex), images.length - 1);
        // Small delay to ensure the block is fully rendered
        setTimeout(() => {
          handleOpenRegenerateDialog(safeIndex);
          // Notify parent that dialog has been opened
          if (onRegenerateDialogOpened) {
            onRegenerateDialogOpened();
          }
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    openRegenerateDialogForBlock, 
    block.id, 
    block.bloc_data?.images?.length, // Use length instead of array reference
    block.content?.images?.length
  ]);

  // Effect to update image titles when images change (e.g., after refresh)
  useEffect(() => {
    if (regenerateDialogOpen && images.length > 0) {
      const titlesMap = new Map(imageTitles);
      images.forEach((image, idx) => {
        // Only update if we don't already have a title for this index (to preserve user edits)
        if (!titlesMap.has(idx) && image.title) {
          titlesMap.set(idx, image.title || '');
        }
      });
      // Only update if there are new titles to add
      if (titlesMap.size > imageTitles.size) {
        setImageTitles(titlesMap);
      }
    }
  }, [images, regenerateDialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset editing state when selected image changes
  useEffect(() => {
    if (editingTitleIndex !== null && editingTitleIndex !== selectedImageIndex) {
      setEditingTitleIndex(null);
    }
  }, [selectedImageIndex, editingTitleIndex]);

  // Fetch image editing models on component mount
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setModelsLoading(true);
        const models = await api.aiModels.getAIModels('image_editing_models');
        const modelsArray = Array.isArray(models) ? models : [];
        setImageEditingModels(modelsArray);
        if (modelsArray.length > 0) {
          setRegenerateModel(modelsArray[0].id);
        }
      } catch (err) {
        console.error('Error fetching image editing models:', err);
        setImageEditingModels([]);
      } finally {
        setModelsLoading(false);
      }
    };

    fetchModels();
  }, []);

  const handleCloseRegenerateDialog = () => {
    setRegenerateDialogOpen(false);
    setRegeneratePrompt('');
    setRegenerateModel('google/gemini-2.5-flash-image');
    setRegenerateStyle('cartoon');
    setRegenerateAspectRatio('16:9');
    setRegenerateNbVariations(2);
    setShowAdvancedParams(false);
  };

  // Handle carousel navigation
  const handleOpenCarousel = (imageIndex) => {
    setCarouselIndex(imageIndex);
    setCarouselOpen(true);
  };

  const handleCloseCarousel = () => {
    setCarouselOpen(false);
    setCarouselIndex(0);
  };

  // Save image title handler
  const handleSaveImageTitle = async (imageIndex) => {
    if (!sessionId || blocIndex === null || blocIndex === undefined || !onSave) return;
    
    const title = imageTitles.get(imageIndex) || '';
    setIsSavingTitle(true);
    
    try {
      const images = block.bloc_data?.images || block.content?.images || [];
      if (imageIndex >= images.length) return;
      
      const updatedImages = images.map((image, idx) => {
        if (idx === imageIndex) {
          return { ...image, title: title.trim() || null };
        }
        return image;
      });
      
      const updatedBlock = {
        ...block,
        bloc_data: {
          ...block.bloc_data,
          images: updatedImages
        }
      };
      
      await onSave(updatedBlock);
      
      // Refresh the seance data
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error saving image title:', error);
      alert('Erreur lors de la sauvegarde du titre. Veuillez réessayer.');
    } finally {
      setIsSavingTitle(false);
    }
  };

  // Toggle favorite handler
  const handleToggleFavorite = async (imageIndex = null) => {
    if (!onSave || !sessionId || blocIndex === null || blocIndex === undefined) return;
    
    const images = block.bloc_data?.images || block.content?.images || [];
    const targetIndex = imageIndex !== null ? imageIndex : selectedImageIndex;
    if (targetIndex >= images.length) return;
    
    const currentImage = images[targetIndex];
    const isFavorite = currentImage?.favourite === true;
    const newFavoriteStatus = !isFavorite;
    
    try {
      // Update image favorite status
      const updatedImages = images.map((image, idx) => {
        if (idx === targetIndex) {
          return { ...image, favourite: newFavoriteStatus };
        }
        return image;
      });
      
      const updatedBlock = {
        ...block,
        bloc_data: {
          ...block.bloc_data,
          images: updatedImages
        }
      };
      
      await onSave(updatedBlock);
      
      // If setting to favorite (true), also add to gallery
      if (newFavoriteStatus) {
        const imageUrl = currentImage?.img_url || currentImage?.compressed_url || currentImage?.url || '';
        const imageTitle = imageTitles.get(targetIndex) || currentImage?.title || '';
        const nameToSend = (imageTitle && imageTitle.trim()) || null;
        
        if (imageUrl) {
          try {
            await api.galleryImage.addImageToGallery(imageUrl, nameToSend);
          } catch (error) {
            console.error('Error adding image to gallery:', error);
            // Don't show error to user, just log it
          }
        }
      }
      
      // Refresh the seance data
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      alert('Erreur lors de la mise à jour du favori. Veuillez réessayer.');
    }
  };

  // Validate/unvalidate the current image directly
  const handleValidateImage = async () => {
    if (!onSave) return;
    
    const images = block.bloc_data?.images || block.content?.images || [];
    if (selectedImageIndex >= images.length) return;
    
    try {
      const currentImage = images[selectedImageIndex];
      const isCurrentlyValidated = currentImage?.status_seance === 'validated';
      
      const updatedImages = images.map((image, index) => {
        if (index === selectedImageIndex) {
          // Toggle validation status
          return { ...image, status_seance: isCurrentlyValidated ? null : 'validated' };
        }
        return image;
      });
      
      const updatedBlock = {
        ...block,
        bloc_data: {
          ...block.bloc_data,
          images: updatedImages
        }
      };
      
      await onSave(updatedBlock);
      
      if (onRefresh) {
        await onRefresh();
      }
      
      // Keep the dialog open - don't close it
    } catch (error) {
      console.error('Error validating image:', error);
      alert('Erreur lors de la validation de l\'image. Veuillez réessayer.');
    }
  };

  const handleRegenerateImage = async () => {
    if (!regeneratePrompt.trim() || !sessionId || blocIndex === null || blocIndex === undefined) return;
    
    setIsRegenerating(true);
    try {
      // Get the actual image index from the selected image
      const selectedImage = images[selectedImageIndex];
      const imageIndex = selectedImage?.image_index ?? selectedImageIndex;
      
      // Call API to regenerate the specific image
      await api.seance.updateImageBlock(
        sessionId,
        blocIndex,
        sequenceIndex,
        imageIndex,
        regeneratePrompt,
        regenerateAspectRatio,
        regenerateModel
      );
      
      // Refresh the block data
      if (onRefresh) {
        await onRefresh();
      }
      
      handleCloseRegenerateDialog();
    } catch (error) {
      console.error('Error regenerating image:', error);
      alert('Erreur lors de la régénération de l\'image. Veuillez réessayer.');
    } finally {
      setIsRegenerating(false);
    }
  };
  
  // Update edited name when musicName changes
  useEffect(() => {
    if (!editingName) {
      setEditedName(musicName);
    }
  }, [musicName, editingName]);
  
  // Handle name editing
  const handleStartEditName = () => {
    setEditingName(true);
    setEditedName(musicName);
  };
  
  const handleCancelEditName = () => {
    setEditingName(false);
    setEditedName(musicName);
  };
  
  const handleSaveName = async () => {
    if (!onSave) return;
    
    setIsSaving(true);
    try {
      const updatedBlock = {
        ...block,
        bloc_data: {
          ...block.bloc_data,
          name: editedName.trim() || 'Sans titre',
          url: musicUrl // Preserve the URL
        }
      };
      
      await onSave(updatedBlock);
      setEditingName(false);
    } catch (error) {
      console.error('Error saving music name:', error);
    } finally {
      setIsSaving(false);
    }
  };
  
  // Image deletion handlers
  const handleOpenDeleteImagesDialog = () => {
    if (selectedImageIndices.size === 0) return;
    setDeleteImagesDialogOpen(true);
  };

  const handleCloseDeleteImagesDialog = () => {
    setDeleteImagesDialogOpen(false);
  };

  const handleDeleteSelectedImages = async () => {
    if (selectedImageIndices.size === 0 || !onSave) return;
    
    setIsDeletingImages(true);
    try {
      // Filter out validated images from selection (shouldn't happen, but safety check)
      const validIndicesToDelete = Array.from(selectedImageIndices).filter(index => {
        const image = images[index];
        return image && image.status_seance !== 'validated';
      });
      
      if (validIndicesToDelete.length === 0) {
        alert('Aucune image non validée à supprimer. Les images validées ne peuvent pas être supprimées.');
        setIsDeletingImages(false);
        return;
      }
      
      // Convert to sorted array to delete from highest index to lowest (to avoid index shifting issues)
      const indicesToDelete = validIndicesToDelete.sort((a, b) => b - a);
      
      // Create new images array without the selected images
      let updatedImages = images.filter((image, index) => !validIndicesToDelete.includes(index));
      
      // Update image_index for each remaining image to be sequential (0, 1, 2, ...)
      updatedImages = updatedImages.map((image, index) => ({
        ...image,
        image_index: index
      }));
      
      // Update block with new images array
      const updatedBlock = {
        ...block,
        bloc_data: {
          ...block.bloc_data,
          images: updatedImages
        }
      };
      
      // Save the updated block
      await onSave(updatedBlock);
      
      // Clear selection, exit delete mode, and close dialog
      setSelectedImageIndices(new Set());
      setIsDeleteMode(false);
      setDeleteImagesDialogOpen(false);
      
      // Clear any pending validations for deleted images
      setPendingImageValidations(prev => {
        const newMap = new Map(prev);
        indicesToDelete.forEach(index => {
          newMap.delete(index);
        });
        return newMap;
      });
      
      // Clear reordered images after deletion
      setReorderedImages(null);
      
      // Refresh the block data
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error deleting images:', error);
      alert('Erreur lors de la suppression des images. Veuillez réessayer.');
    } finally {
      setIsDeletingImages(false);
    }
  };
  
  return (
    <Box sx={{ 
      position: 'relative', 
      width: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Music Name Section */}
      <Paper 
        elevation={2}
        sx={{ 
          p: 2, 
          mb: 2, 
          borderRadius: 2,
          bgcolor: 'background.paper'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <MusicNoteIcon sx={{ fontSize: '2rem', color: 'primary.main' }} />
          <Box sx={{ flex: 1 }}>
            {editingName ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  fullWidth
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveName();
                    } else if (e.key === 'Escape') {
                      handleCancelEditName();
                    }
                  }}
                  autoFocus
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    }
                  }}
                />
                <IconButton
                  onClick={handleSaveName}
                  disabled={isSaving}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    }
                  }}
                >
                  {isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                </IconButton>
                <IconButton
                  onClick={handleCancelEditName}
                  disabled={isSaving}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <CancelIcon />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'text.primary',
                    fontWeight: 'bold',
                    flex: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      color: 'primary.main'
                    }
                  }}
                  onDoubleClick={handleStartEditName}
                >
                  {musicName}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleStartEditName}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      color: 'primary.main'
                    }
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
      
      {/* Audio Player */}
      {musicUrl && (
        <Paper 
          elevation={2}
          sx={{ 
            p: 2, 
            mb: 2,
            borderRadius: 2,
            bgcolor: 'background.paper'
          }}
        >
          <audio 
            controls 
            style={{ width: '100%' }}
            src={musicUrl}
          >
            Votre navigateur ne supporte pas l'élément audio.
          </audio>
        </Paper>
      )}
      
      {/* ImageGrid Section */}
      {images.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {/* Delete mode buttons and validation action buttons */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {isDeleteMode && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setIsDeleteMode(false);
                    setSelectedImageIndices(new Set());
                  }}
                  sx={{
                    borderColor: '#95A5A6',
                    color: '#7F8C8D',
                    borderRadius: '20px',
                    textTransform: 'none'
                  }}
                >
                  Annuler
                </Button>
                
                {selectedImageIndices.size > 0 && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={handleOpenDeleteImagesDialog}
                    disabled={isDeletingImages}
                    sx={{
                      background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
                      borderRadius: '20px',
                      textTransform: 'none'
                    }}
                  >
                    Confirmer la suppression ({selectedImageIndices.size})
                  </Button>
                )}
              </>
            )}
            
            {/* Save button - Available when there are pending validations or reordering */}
            {(pendingImageValidations.size > 0 || reorderedImages) && (
              <Button
                variant="contained"
                size="small"
                startIcon={isSavingValidation ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSaveValidations}
                disabled={isSavingValidation}
                sx={{
                  background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
                  borderRadius: '20px',
                  textTransform: 'none',
                  ml: 'auto'
                }}
              >
                {isSavingValidation ? 'Sauvegarde...' : `Sauvegarder (${pendingImageValidations.size + (reorderedImages ? 1 : 0)})`}
              </Button>
            )}
          </Box>
          
          <ImageGrid
            images={images}
            imageFilter={imageFilter}
            isDeleteMode={isDeleteMode}
            selectedImageIndices={selectedImageIndices}
            pendingImageValidations={pendingImageValidations}
            draggedImageIndex={draggedImageIndex}
            dragOverIndex={dragOverIndex}
            getEffectiveValidationStatus={getEffectiveValidationStatus}
            getOriginalIndex={getOriginalIndex}
            handleDragStart={handleDragStart}
            handleDragOver={handleDragOver}
            handleDragLeave={handleDragLeave}
            handleDrop={handleDrop}
            handleDragEnd={handleDragEnd}
            handleToggleImageSelection={handleToggleImageSelection}
            handleToggleImageValidation={handleToggleImageValidation}
            handleOpenRegenerateDialog={handleOpenRegenerateDialog}
            imageUrlField="img_url"
            imageMenuAnchor={imageMenuAnchor}
            onMenuOpen={(e) => setImageMenuAnchor(e.currentTarget)}
            onMenuClose={() => setImageMenuAnchor(null)}
            onFilterChange={(filter) => setImageFilter(filter)}
            onDeleteModeToggle={() => setIsDeleteMode(!isDeleteMode)}
          />
        </Box>
      )}
      
      {!musicUrl && (
        <Paper 
          elevation={1}
          sx={{ 
            p: 4, 
            textAlign: 'center',
            borderRadius: 2,
            bgcolor: 'background.paper'
          }}
        >
          <MusicNoteIcon sx={{ fontSize: '4rem', color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            Aucune musique disponible
          </Typography>
        </Paper>
      )}

      {/* Regenerate Image Dialog */}
      <Dialog
        open={regenerateDialogOpen}
        onClose={handleCloseRegenerateDialog}
        maxWidth={aiChatOpen ? false : "lg"}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            border: '2px solid #4A90E2',
            maxWidth: aiChatOpen ? `calc(100% - 64px)` : '1200px',
            width: aiChatOpen ? `calc(1200px + ${DRAWER_WIDTH}px)` : 'auto',
            transition: 'width 0.3s ease, max-width 0.3s ease'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2,
          position: 'relative'
        }}>
          <IconButton
            onClick={handleCloseRegenerateDialog}
            size="large"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: '#E74C3C',
              '&:hover': {
                backgroundColor: 'rgba(231, 76, 60, 0.1)',
                color: '#C0392B'
              },
              '&:focus': {
                color: '#E74C3C'
              }
            }}
          >
            <CloseIcon sx={{ fontSize: 32 }} />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, pr: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '20px' }}>
                Régénérer l'image
              </Typography>
              <Button
                onClick={() => setAiChatOpen(!aiChatOpen)}
                variant={aiChatOpen ? "contained" : "outlined"}
                startIcon={<SmartToyIcon />}
                size="small"
                sx={{
                  borderRadius: '20px',
                  textTransform: 'none',
                  ...(aiChatOpen && {
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                    }
                  })
                }}
              >
                Chat IA
              </Button>
              <Button
                onClick={handleValidateImage}
                variant="contained"
                startIcon={<CheckCircle />}
                size="small"
                sx={{
                  background: (() => {
                    const images = block.bloc_data?.images || block.content?.images || [];
                    const currentImage = images[selectedImageIndex];
                    const isValidated = currentImage?.status_seance === 'validated';
                    return isValidated 
                      ? 'linear-gradient(135deg, #E74C3C 0%, #C0392B 100%)'
                      : 'linear-gradient(135deg, #27AE60 0%, #229954 100%)';
                  })(),
                  borderRadius: '20px',
                  px: 2,
                  py: 0.75,
                  fontWeight: 'bold',
                  color: 'white',
                  textTransform: 'none',
                  '&:hover': {
                    background: (() => {
                      const images = block.bloc_data?.images || block.content?.images || [];
                      const currentImage = images[selectedImageIndex];
                      const isValidated = currentImage?.status_seance === 'validated';
                      return isValidated 
                        ? 'linear-gradient(135deg, #C0392B 0%, #A93226 100%)'
                        : 'linear-gradient(135deg, #229954 0%, #1E8449 100%)';
                    })(),
                    transform: 'translateY(-2px)',
                    boxShadow: (() => {
                      const images = block.bloc_data?.images || block.content?.images || [];
                      const currentImage = images[selectedImageIndex];
                      const isValidated = currentImage?.status_seance === 'validated';
                      return isValidated 
                        ? '0 8px 25px rgba(231, 76, 60, 0.4)'
                        : '0 8px 25px rgba(39, 174, 96, 0.4)';
                    })()
                  }
                }}
              >
                {(() => {
                  const images = block.bloc_data?.images || block.content?.images || [];
                  const currentImage = images[selectedImageIndex];
                  const isValidated = currentImage?.status_seance === 'validated';
                  return isValidated ? 'Invalider' : 'Valider';
                })()}
              </Button>
            </Box>
            
            {/* Controls Section */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', flex: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
              <Button
                onClick={() => setShowAdvancedParams(!showAdvancedParams)}
                variant="outlined"
                size="small"
                startIcon={showAdvancedParams ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                endIcon={<SettingsIcon />}
                sx={{
                  borderRadius: '20px',
                  textTransform: 'none',
                  borderColor: '#6b7280',
                  color: '#6b7280',
                  '&:hover': {
                    borderColor: '#4b5563',
                    backgroundColor: '#f9fafb'
                  }
                }}
              >
                Paramètres avancés
              </Button>
              
              {showAdvancedParams && (
                <>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Modèle</InputLabel>
                    <Select
                      value={regenerateModel}
                      label="Modèle"
                      onChange={(e) => setRegenerateModel(e.target.value)}
                      sx={{ borderRadius: '8px' }}
                      disabled={modelsLoading}
                    >
                      {imageEditingModels.map((model) => (
                        <MenuItem key={model.id} value={model.id}>
                          {model.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Style</InputLabel>
                    <Select
                      value={regenerateStyle}
                      label="Style"
                      onChange={(e) => setRegenerateStyle(e.target.value)}
                      sx={{ borderRadius: '8px' }}
                    >
                      <MenuItem value="cartoon">Cartoon</MenuItem>
                      <MenuItem value="realistic">Réaliste</MenuItem>
                      <MenuItem value="semi-realistic">Semi-réaliste</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Ratio</InputLabel>
                    <Select
                      value={regenerateAspectRatio}
                      label="Ratio"
                      onChange={(e) => setRegenerateAspectRatio(e.target.value)}
                      sx={{ borderRadius: '8px' }}
                    >
                      <MenuItem value="16:9">16:9</MenuItem>
                      <MenuItem value="1:1">1:1</MenuItem>
                      <MenuItem value="9:16">9:16</MenuItem>
                      <MenuItem value="4:3">4:3</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Nb. Variation</InputLabel>
                    <Select
                      value={regenerateNbVariations}
                      label="Nb. Variation"
                      onChange={(e) => setRegenerateNbVariations(e.target.value)}
                      sx={{ borderRadius: '8px' }}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                        <MenuItem key={num} value={num}>{num}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ 
          display: 'flex', 
          gap: 2, 
          p: 3,
          position: 'relative',
          overflow: 'hidden',
          height: 'calc(100vh - 250px)',
          maxHeight: 'calc(100vh - 250px)',
          minHeight: '550px'
        }}>
          <Box sx={{ 
            flex: 1, 
            minWidth: 0,
            transition: 'flex 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            pr: 1,
            height: '100%',
            maxHeight: '100%'
          }}>
            {/* Bloc Content - Scrollable Container */}
            <Box sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              overflowX: 'hidden',
              minHeight: 0,
              pr: 1
            }}>
              {/* Block Information Section - Music */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2, color: '#34495E' }}>
                  Informations du bloc
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box
                    sx={{
                      padding: '16px',
                      backgroundColor: '#e3f2fd',
                      borderRadius: '8px',
                      border: '1px solid #bbdefb'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <MusicNoteIcon sx={{ fontSize: '1.5rem', color: '#4A90E2' }} />
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 'bold', 
                          color: '#2C3E50',
                          marginBottom: '8px'
                        }}
                      >
                        Musique
                      </Typography>
                    </Box>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#2C3E50', 
                        lineHeight: 1.6,
                        marginBottom: '12px'
                      }}
                    >
                      {musicName}
                    </Typography>
                    {musicUrl && (
                      <Box sx={{ mt: 1 }}>
                        <audio 
                          controls 
                          style={{ width: '100%' }}
                          src={musicUrl}
                        >
                          Votre navigateur ne supporte pas l'élément audio.
                        </audio>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Prompt and Image Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', flexDirection: { xs: 'column', md: 'row' } }}>
                  <Box sx={{ flex: { xs: '1 1 auto', md: '1 1 40%' }, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={12}
                      value={regeneratePrompt}
                      onChange={(e) => setRegeneratePrompt(e.target.value)}
                      placeholder="Prompt de generation d'image"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          border: '2px solid #000',
                          '& textarea': {
                            overflow: 'auto !important'
                          }
                        }
                      }}
                    />
                    {/* Régénérer button under prompt */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', flexShrink: 0 }}>
                      <Button
                        onClick={handleRegenerateImage}
                        variant="contained"
                        disabled={!regeneratePrompt.trim() || isRegenerating || !regenerateModel}
                        startIcon={isRegenerating ? <CircularProgress size={20} color="inherit" /> : <ImageIcon />}
                        sx={{
                          background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
                          borderRadius: '25px',
                          px: 3,
                          py: 1,
                          fontWeight: 'bold',
                          color: 'white',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #357ABD 0%, #2E6DA4 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(74, 144, 226, 0.4)'
                          },
                          '&:disabled': {
                            background: '#BDBDBD',
                            transform: 'none',
                            boxShadow: 'none'
                          }
                        }}
                      >
                        {isRegenerating ? 'Régénération...' : `Régénérer l'image ${selectedImageIndex + 1}`}
                      </Button>
                    </Box>
                  </Box>
                  <Box sx={{ flex: { xs: '1 1 auto', md: '1 1 60%' }, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    {/* Image Counter and Title on same row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      {/* Image Title - Editable on double-click */}
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flex: 1, minHeight: '32px', maxWidth: '100%' }}>
                      {editingTitleIndex === selectedImageIndex ? (
                        <TextField
                          autoFocus
                          value={imageTitles.get(selectedImageIndex) || ''}
                          onChange={(e) => {
                            const newTitles = new Map(imageTitles);
                            newTitles.set(selectedImageIndex, e.target.value);
                            setImageTitles(newTitles);
                          }}
                          onBlur={() => {
                            handleSaveImageTitle(selectedImageIndex);
                            setEditingTitleIndex(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveImageTitle(selectedImageIndex);
                              setEditingTitleIndex(null);
                            } else if (e.key === 'Escape') {
                              setEditingTitleIndex(null);
                            }
                          }}
                          placeholder="Titre de l'image"
                          size="small"
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '6px',
                              fontSize: '0.875rem',
                              height: '32px',
                              '& input': {
                                py: 0.5,
                              }
                            }
                          }}
                        />
                      ) : (
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            gap: 0.5, 
                            alignItems: 'center', 
                            flex: 1,
                            cursor: 'pointer',
                            '&:hover': {
                              '& .title-text': {
                                color: '#4A90E2',
                              }
                            }
                          }}
                          onDoubleClick={() => setEditingTitleIndex(selectedImageIndex)}
                        >
                          <Typography 
                            variant="body1" 
                            className="title-text"
                            sx={{ 
                              color: imageTitles.get(selectedImageIndex) ? '#2C3E50' : '#9CA3AF',
                              fontStyle: imageTitles.get(selectedImageIndex) ? 'normal' : 'italic',
                              fontWeight: 'bold',
                              fontSize: '1rem',
                              flex: 1,
                              transition: 'color 0.2s'
                            }}
                          >
                            {imageTitles.get(selectedImageIndex) || 'Double-cliquez pour ajouter un titre'}
                          </Typography>
                        </Box>
                      )}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#34495E', whiteSpace: 'nowrap' }}>
                        Image {selectedImageIndex + 1} / {images.length}
                      </Typography>
                    </Box>
                    <Box
                      onClick={() => handleOpenCarousel(selectedImageIndex)}
                      sx={{
                        minWidth: 0,
                        minHeight: '400px',
                        maxHeight: '600px',
                        border: (() => {
                          const images = block.bloc_data?.images || block.content?.images || [];
                          const currentImage = images[selectedImageIndex];
                          const isValidated = currentImage?.status_seance === 'validated';
                          return isValidated ? '4px solid #27AE60' : '2px solid #000';
                        })(),
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#F0F0F0',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: (() => {
                          const images = block.bloc_data?.images || block.content?.images || [];
                          const currentImage = images[selectedImageIndex];
                          const isValidated = currentImage?.status_seance === 'validated';
                          return isValidated ? '0 0 0 3px rgba(39, 174, 96, 0.2)' : 'none';
                        })(),
                        cursor: 'pointer',
                        '&:hover .carousel-nav-button': {
                          opacity: 1
                        },
                        '&:hover': {
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgcolor: 'rgba(0, 0, 0, 0.1)',
                            transition: 'background-color 0.2s ease',
                            pointerEvents: 'none'
                          }
                        }
                      }}
                    >
                      {images.length > 1 && (
                        <>
                          <IconButton
                            className="carousel-nav-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
                            }}
                            sx={{
                              position: 'absolute',
                              left: 4,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              zIndex: 2,
                              bgcolor: 'rgba(0, 0, 0, 0.5)',
                              color: 'white',
                              opacity: 0.3,
                              transition: 'opacity 0.2s ease',
                              width: 32,
                              height: 32,
                              '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.7)',
                                opacity: 1
                              }
                            }}
                          >
                            <NavigateBefore sx={{ fontSize: '1.2rem' }} />
                          </IconButton>
                          <IconButton
                            className="carousel-nav-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
                            }}
                            sx={{
                              position: 'absolute',
                              right: 4,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              zIndex: 2,
                              bgcolor: 'rgba(0, 0, 0, 0.5)',
                              color: 'white',
                              opacity: 0.3,
                              transition: 'opacity 0.2s ease',
                              width: 32,
                              height: 32,
                              '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.7)',
                                opacity: 1
                              }
                            }}
                          >
                            <NavigateNext sx={{ fontSize: '1.2rem' }} />
                          </IconButton>
                        </>
                      )}
                      {(() => {
                        const selectedImage = images[selectedImageIndex];
                        const imageUrl = selectedImage?.img_url || selectedImage?.compressed_url || selectedImage?.url;
                        return imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={`Image ${selectedImageIndex + 1}`}
                            style={{
                              maxWidth: '100%',
                              height: 'auto',
                              objectFit: 'contain',
                              pointerEvents: 'none',
                              display: 'block'
                            }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ color: '#7F8C8D' }}>
                            IMAGE
                          </Typography>
                        );
                      })()}
                      
                      {/* Favorite button on image in regeneration dialog - top-left corner */}
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFavorite(selectedImageIndex);
                        }}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          zIndex: 10,
                          bgcolor: 'white',
                          width: '36px',
                          height: '36px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                          color: (() => {
                            const images = block.bloc_data?.images || block.content?.images || [];
                            const currentImage = images[selectedImageIndex];
                            return currentImage?.favourite === true ? '#FFD700' : '#BDC3C7';
                          })(),
                          '&:hover': {
                            bgcolor: '#F5F5F5',
                            transform: 'scale(1.1)',
                            color: '#FFD700'
                          }
                        }}
                      >
                        {(() => {
                          const images = block.bloc_data?.images || block.content?.images || [];
                          const currentImage = images[selectedImageIndex];
                          return currentImage?.favourite === true ? (
                            <StarIcon sx={{ fontSize: '28px' }} />
                          ) : (
                            <StarBorderIcon sx={{ fontSize: '28px' }} />
                          );
                        })()}
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          
          {/* AI Chat Sidebar */}
          {aiChatOpen && (
            <Box sx={{ 
              width: DRAWER_WIDTH, 
              flexShrink: 0,
              borderLeft: '1px solid #e0e0e0',
              ml: 2,
              height: '100%',
              maxHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: '100%',
                maxHeight: '100%',
                '& > div': {
                  position: 'relative !important',
                  top: '0 !important',
                  height: '100% !important',
                  maxHeight: '100% !important',
                  flex: '1 1 auto !important',
                  overflow: 'hidden !important',
                  '&:first-of-type': {
                    height: '100% !important',
                    maxHeight: '100% !important',
                    display: 'flex !important',
                    flexDirection: 'column !important',
                    boxShadow: 'none !important',
                    borderLeft: 'none !important',
                    position: 'relative !important',
                    alignSelf: 'auto !important'
                  }
                }
              }}>
                <AIChat
                  open={aiChatOpen}
                  onClose={() => setAiChatOpen(false)}
                  sessionId={sessionId}
                  sequenceIndex={sequenceIndex}
                  blocIndex={blocIndex}
                  blockType="music"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Carousel Dialog */}
      <Dialog
        open={carouselOpen}
        onClose={handleCloseCarousel}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            bgcolor: '#000',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', minHeight: '500px' }}>
          <IconButton
            onClick={handleCloseCarousel}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          
          {images.length > 0 && (
            <>
              {carouselIndex > 0 && (
                <IconButton
                  onClick={() => setCarouselIndex(carouselIndex - 1)}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    color: 'white',
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.7)'
                    }
                  }}
                >
                  <NavigateBefore />
                </IconButton>
              )}
              {carouselIndex < images.length - 1 && (
                <IconButton
                  onClick={() => setCarouselIndex(carouselIndex + 1)}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    color: 'white',
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.7)'
                    }
                  }}
                >
                  <NavigateNext />
                </IconButton>
              )}
            </>
          )}

          {images.length > 0 && images[carouselIndex] && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              minHeight: '500px',
              p: 2,
              position: 'relative'
            }}>
              <Box
                component="img"
                src={images[carouselIndex].img_url || images[carouselIndex].compressed_url || images[carouselIndex].url}
                alt={`Image ${carouselIndex + 1}`}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '85vh',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}

          <Box sx={{ textAlign: 'center', mt: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ color: 'white' }}>
              {carouselIndex + 1} / {images.length}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
      
      {/* Delete Images Confirmation Dialog */}
      <Dialog
        open={deleteImagesDialogOpen}
        onClose={handleCloseDeleteImagesDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            '& .MuiDialog-paper': {
              borderRadius: '20px'
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
          color: 'white',
          borderRadius: '20px 20px 0 0'
        }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <WarningIcon />
            Confirmation de suppression
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2C3E50' }}>
            Êtes-vous sûr de vouloir supprimer {selectedImageIndices.size} image{selectedImageIndices.size > 1 ? 's' : ''} ?
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
            Cette action est irréversible. Les images sélectionnées seront définitivement supprimées.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
          <Button
            onClick={handleCloseDeleteImagesDialog}
            variant="outlined"
            disabled={isDeletingImages}
            sx={{
              borderRadius: '25px',
              px: 3,
              py: 1,
              borderColor: '#95A5A6',
              color: '#7F8C8D',
              '&:hover': {
                borderColor: '#7F8C8D',
                backgroundColor: '#F8F9FA'
              }
            }}
          >
            Annuler
          </Button>
          
          <Button
            onClick={handleDeleteSelectedImages}
            variant="contained"
            disabled={isDeletingImages}
            startIcon={isDeletingImages ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            sx={{
              background: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)',
              borderRadius: '25px',
              px: 3,
              py: 1,
              fontWeight: 'bold',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #C62828 0%, #B71C1C 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(229, 57, 53, 0.4)'
              },
              '&:disabled': {
                background: '#BDBDBD',
                transform: 'none',
                boxShadow: 'none'
              }
            }}
          >
            {isDeletingImages ? 'Suppression...' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

